import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { LEAD_STATUSES, SOURCE_OPTIONS } from "@/lib/leadConstants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LeadDialog({ open, onOpenChange, lead, team = [], onSaved }) {
  const editing = !!lead;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => ({
    name: lead?.name || "", phone: lead?.phone || "", email: lead?.email || "",
    service: lead?.service || "", problem_description: lead?.problem_description || "",
    location: lead?.location || "", zip_code: lead?.zip_code || "",
    source: lead?.source || "Manual", status: lead?.status || "New",
    notes: lead?.notes || "", assigned_to: lead?.assigned_to || "unassigned",
    estimated_value: lead?.estimated_value || 0,
  }));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setLoading(true);
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0, assigned_to: form.assigned_to === "unassigned" ? null : form.assigned_to };
      if (editing) await api.put(`/leads/${lead.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(editing ? "Lead updated" : "Lead created");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit lead" : "New lead"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={set("name")} data-testid="lead-name" className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="lead-phone" className="mt-1" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={set("email")} data-testid="lead-email" className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Service requested</Label><Input value={form.service} onChange={set("service")} data-testid="lead-service" className="mt-1" /></div>
            <div><Label>Estimated value ($)</Label><Input type="number" value={form.estimated_value} onChange={set("estimated_value")} data-testid="lead-value" className="mt-1" /></div>
          </div>
          <div><Label>Problem description</Label><Textarea value={form.problem_description} onChange={set("problem_description")} data-testid="lead-problem" className="mt-1" rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Location</Label><Input value={form.location} onChange={set("location")} data-testid="lead-location" className="mt-1" /></div>
            <div><Label>ZIP</Label><Input value={form.zip_code} onChange={set("zip_code")} data-testid="lead-zip" className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="lead-status"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="mt-1" data-testid="lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Assigned to</Label>
            <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
              <SelectTrigger className="mt-1" data-testid="lead-assigned"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} data-testid="lead-notes" className="mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={loading} data-testid="lead-save-btn" className="bg-blue-600 hover:bg-blue-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
